import { Layout, MenuProps, Dropdown, Avatar } from "antd";
import { LayoutWrapper } from "./styles";
import { UserOutlined, LogoutOutlined, HomeOutlined, SettingOutlined, EyeOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import Paragraph from "components/assets/text/Paragraph";
import { useAppDispatch, useAppSelector, useDarkMode, useThemeColors } from "redux/hooks";
import { signOut, setAdminViewModalOpen } from "redux/slices/user";
import { requestStart } from "redux/slices/app";
import Model from "redux/classes/Model";
import Job from "redux/classes/Job";
import VELOCITY from "components/icons/brand/VELOCITY";
import { useSession } from "../SessionProvider";
import { SettingsView } from "components/assets/utilities/SettingsView";
import { getInitials } from "components/assets/utilities/avatarUtils";

const { Content } = Layout;
const { Header } = Layout;

interface LayoutPropsTypes {
  children: JSX.Element;
}

const CustomLayout: React.FC<LayoutPropsTypes> = ({ children }) => {
  const dispatch = useAppDispatch()
  const user = useSelector((state: any) => state.user)
  const app = useSelector((state: any) => state.app)
  const session = useSession();
  const darkMode = useDarkMode();
  const themeColors = useThemeColors();
  const isAdmin = useAppSelector(state => state.user.settings?.is_admin ?? false);
  const adminViewUserID = useAppSelector(state => state.user.adminViewUserID);
  const adminViewUser = useAppSelector(state => state.user.users.find(u => u.userID === state.user.adminViewUserID));
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const modelIndex = user.modelIndex;
  const jobIndex = user.jobIndex;

  let selectedModel = new Model("", "", "", "", "", "", "", [], [], [], [], "");
  let selectedJob = new Job("", "", "", "", "", "", "", "", "", "", new Date(), new Date(), new Date(), "");

  if (modelIndex !== -1) {
    selectedModel = user.models[modelIndex];
  }
  if (jobIndex !== -1) {
    selectedJob = user.jobs[jobIndex];
  }

  // Page name on Header
  let homeText = <Link
    to="/"
    className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
    title={app.loading || app.submitting ? "Cannot navigate between pages while loading": "Links to the Home Page"}
  >
    Home
  </Link>;
  const pageLocation = useLocation().pathname;
  let header = <></>;

  // Check if we're on RunPageSelector route (/:modelName) and model alias starts with 'QST_' or 'DASH' or 'QUERYSTREAM_' or 'DASHSTREAM_'
  const pathSegments = pageLocation.split('/').filter(segment => segment !== '');
  const isRunPageSelectorRoute = pathSegments.length === 1 && pageLocation !== '/' && pageLocation !== '/chat';
  const modelAliasFromPath = isRunPageSelectorRoute ? decodeURIComponent(pathSegments[0]) : null;
  const shouldHideHeader = isRunPageSelectorRoute && modelAliasFromPath && (modelAliasFromPath.startsWith('QST_') || modelAliasFromPath.startsWith('DASH') || modelAliasFromPath.startsWith('QUERYSTREAM_') || modelAliasFromPath.startsWith('DASHSTREAM_'));

  if (pageLocation === `/`) {
    header = (
      <div className="breadcrumbs">
        <div
          className="breadcrumb"
          title="You are on the Home Page"
        >Home</div>
      </div>
    );
  }
  else if (pageLocation === `/${encodeURIComponent(selectedModel.alias)}/info`) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the Model Page for " + selectedModel.name}
        >{selectedModel.name}</div>
      </div>
    );
  }
  else if (pageLocation === `/${encodeURIComponent(selectedModel.alias)}/qa`) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/info`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={app.loading || app.submitting ? "Cannot navigate between pages while loading": "Links to the Model Page for " + selectedModel.name}
        >
          {selectedModel.name}
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the QA Checks Page for " + selectedModel.name}
        >QA Checks</div>
      </div>
    );
  }
  else if (pageLocation === `/${encodeURIComponent(selectedModel.alias)}/flow`) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/info`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={app.loading || app.submitting ? "Cannot navigate between pages while loading": "Links to the Model Page for " + selectedModel.name}
        >
          {selectedModel.name}
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the Model Flow Page for " + selectedModel.name}
        >Model Flow</div>
      </div>
    );
  }
  else if (pageLocation === `/${encodeURIComponent(selectedModel.alias)}/io`) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/info`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={app.loading || app.submitting ? "Cannot navigate between pages while loading": "Links to the Model Page for " + selectedModel.name}
        >
          {selectedModel.name}
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the Inputs & Outputs Page for " + selectedModel.name}
        >Inputs & Outputs</div>
      </div>
    );
  }
  else if (pageLocation === `/${encodeURIComponent(selectedModel.alias)}/feedback`) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/info`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={app.loading || app.submitting ? "Cannot navigate between pages while loading": "Links to the Model Page for " + selectedModel.name}
        >
          {selectedModel.name}
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the Model Feedback Page for " + selectedModel.name}
        >Feedback</div>
      </div>
    );
  }
  else if (
    pageLocation === `/${encodeURIComponent(selectedModel.alias)}/run` ||
    pageLocation === `/${encodeURIComponent(selectedModel.alias)}`
  ) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/info`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={app.loading || app.submitting ? "Cannot navigate between pages while loading": "Links to the Model Page for " + selectedModel.name}
        >
          {selectedModel.name}
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the Run Page for " + selectedModel.name}
        >Run</div>
      </div>
    );
  }
  else if (pageLocation === `/${encodeURIComponent(selectedModel.alias)}/job/${selectedJob.jobID}`) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/info`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={"Links to the Model Page for " + selectedModel.name}
        >
          {selectedModel.name}
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the Job Page for " + selectedJob.jobName}
        >Job</div>
        {selectedJob.status !== "RUNNING" &&
          <>
            <div className="breadcrumb">{" > "}</div>
            <Link
              to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/results/${selectedJob.jobID}`}
              className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
              title={"Links to the Result Page for " + selectedJob.resultName}
              onClick={() =>  dispatch(requestStart())}
            >
              Result
            </Link>
          </>
        }
      </div>
    );
  }
  else if (pageLocation === `/${encodeURIComponent(selectedModel.alias)}/results/${selectedJob.jobID}`) {
    header = (
      <div className="breadcrumbs">
        {homeText}
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/info`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={"Links to the Model Page for " + selectedModel.name}
        >
          {selectedModel.name}
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <Link
          to={app.loading || app.submitting ? pageLocation : `/${encodeURIComponent(selectedModel.alias)}/job/${selectedJob.jobID}`}
          className={app.loading || app.submitting ? "disabled breadcrumb" : "breadcrumb"}
          title={"Links to the Job Page for " + selectedJob.jobName}
        >
          Job
        </Link>
        <div className="breadcrumb">{" > "}</div>
        <div
          className="breadcrumb"
          title={"You are on the Result Page for " + selectedJob.resultName}
        >Result</div>
      </div>
    );
  }

  const items: MenuProps['items'] = [
    {
      key: "username",
      label: (
        <div style={{ padding: "8px 12px", fontWeight: "bold", color: themeColors.textColor, pointerEvents: "none" }}>
          {session.userDisplayName}
        </div>
      ),
      disabled: true,
      style: { cursor: "default" },
    },
    ...(isAdmin ? [{
      key: "home",
      icon: <HomeOutlined />,
      label: (
        <a
          href="/"
          onClick={(e) => {
            if (!e.metaKey && !e.ctrlKey) {
              e.preventDefault();
              window.location.href = '/';
            }
          }}
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          Home
        </a>
      ),
    }] : []),
    {
      type: 'divider',
    },
    ...(isAdmin ? [{
      key: "adminView",
      icon: <EyeOutlined />,
      label: adminViewUserID ? `Admin View: ${adminViewUser?.name || adminViewUserID}` : "Admin View",
      onClick: () => {
        dispatch(setAdminViewModalOpen({ open: true }));
      },
    }] : []),
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "Settings",
      disabled: !!adminViewUserID,
      onClick: () => {
        if (adminViewUserID) return;
        setIsSettingsOpen(true);
      },
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log Out",
      onClick: () => {
        dispatch(signOut());
        session.logout();
      },
    },
  ];

  return (
    <LayoutWrapper>
      <Layout className="layout-container fill-viewport">
        {!shouldHideHeader && <Header className="header-bg-color">
          <div className="header">
            <div className="flex">
              <Link
                to={app.loading || app.submitting ? pageLocation : "/"}
                className={app.loading || app.submitting ? "disabled" : ""}
              >
                <div className={pageLocation !== '/' ? "clickable" : ""}><VELOCITY color={themeColors.primaryColor} /></div>
              </Link>

              <Paragraph className="color-header text">{header}</Paragraph>
            </div>

            <div className="user-profile">
              <style>{`
                ${darkMode ? `
                    .user-menu-dropdown .ant-dropdown-menu {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item-divider {
                        background-color: ${themeColors.borderColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item-icon {
                        color: ${themeColors.textColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item a {
                        color: ${themeColors.textColor} !important;
                    }
                ` : ''}
              `}</style>
              <Dropdown
                trigger={["click"]}
                menu={{ items }}
                placement="bottomRight"
                overlayStyle={{ marginTop: "8px" }}
                overlayClassName={darkMode ? "user-menu-dropdown" : ""}
              >
                <div
                  style={{
                    display: "inline-block",
                    borderRadius: "50%",
                    boxShadow: adminViewUserID ? `0 0 0 3px ${themeColors.primaryColorHover}` : "none",
                    transition: "box-shadow 0.2s ease",
                  }}
                  title={adminViewUserID ? `Admin View — ${adminViewUser?.name || adminViewUserID}` : session.userDisplayName}
                >
                  <div style={{ cursor: "pointer", lineHeight: 0 }}>
                    {adminViewUserID ? (
                      <Avatar size={48} style={{ backgroundColor: themeColors.primaryColor, color: "#ffffff", fontWeight: 600 }}>
                        {getInitials(adminViewUser?.name || adminViewUserID)}
                      </Avatar>
                    ) : session.userPicture ? (
                      <Avatar src={session.userPicture} size={48} />
                    ) : (
                      <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: themeColors.primaryColor }} />
                    )}
                  </div>
                </div>
              </Dropdown>
            </div>
          </div>
        </Header>}
        <Layout className="site-layout">
          <Content className="content-padding flex-column">{children}</Content>
        </Layout>
      </Layout>
      <SettingsView isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} mode="dashboard" />
    </LayoutWrapper>
  );
};

export default CustomLayout;
